import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API, axios } from "@/api";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { ArrowLeft, MapPin, Loader2, Navigation, Clock, Zap, Truck } from "lucide-react";
import { motion } from "framer-motion";
import { BottomNav } from "@/components/shared/Navigation";

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Custom live truck pin
const liveTruckIcon = new L.DivIcon({
  className: "live-truck-pin",
  html: `<div style="
    width:38px;height:38px;border-radius:50%;
    background:linear-gradient(135deg,#22C55E,#16A34A);
    border:3px solid white;box-shadow:0 2px 12px rgba(34,197,94,0.5);
    display:flex;align-items:center;justify-content:center;
    position:relative;
  ">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
      <rect x="1" y="3" width="15" height="13" rx="2" ry="2"/>
      <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/>
      <circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
    </svg>
    <div style="
      position:absolute;top:-3px;right:-3px;width:10px;height:10px;
      border-radius:50%;background:#22C55E;border:2px solid white;
      animation:pulse 1.5s infinite;
    "></div>
  </div>
  <style>
    @keyframes pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.6;transform:scale(1.3)} }
  </style>`,
  iconSize: [38, 38],
  iconAnchor: [19, 38],
  popupAnchor: [0, -40],
});

// User location pin
const userIcon = new L.DivIcon({
  className: "user-loc-pin",
  html: `<div style="
    width:16px;height:16px;border-radius:50%;
    background:#3B82F6;border:3px solid white;
    box-shadow:0 0 0 6px rgba(59,130,246,0.2), 0 2px 8px rgba(0,0,0,0.2);
  "></div>`,
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

function RecenterMap({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.setView([center.lat, center.lng], zoom || 12);
  }, [center, zoom, map]);
  return null;
}

function TruckPopup({ pin, onNavigate }) {
  const liveFor = pin.went_live_at
    ? Math.round((Date.now() - new Date(pin.went_live_at).getTime()) / 60000)
    : null;

  return (
    <div className="w-56 -m-1">
      {/* Address arrow indicator */}
      <div className="flex items-start gap-2 mb-2">
        <div className="mt-0.5 shrink-0">
          <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
            <Navigation className="w-3 h-3 text-white" style={{ transform: "rotate(45deg)" }} />
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold text-slate-800 leading-tight">{pin.address || "Location shared"}</p>
          {pin.city && <p className="text-[9px] text-slate-400">{pin.city}</p>}
        </div>
      </div>

      {/* Truck info */}
      <div
        className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition"
        onClick={() => onNavigate(pin.business_id)}
      >
        {pin.logo_url ? (
          <img src={pin.logo_url} alt="" className="w-8 h-8 rounded-lg object-contain bg-white" />
        ) : (
          <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
            <Truck className="w-4 h-4 text-amber-600" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-xs font-bold text-slate-800 truncate">{pin.name}</p>
          {pin.cuisine_tags?.length > 0 && (
            <p className="text-[9px] text-slate-400 truncate">{pin.cuisine_tags.slice(0, 3).join(" · ")}</p>
          )}
        </div>
      </div>

      {/* Stats row */}
      <div className="flex items-center gap-3 mt-2 text-[10px] text-slate-500">
        {liveFor !== null && (
          <span className="flex items-center gap-0.5">
            <Clock className="w-3 h-3 text-green-500" />
            Live {liveFor}m
          </span>
        )}
        {pin.wait_minutes != null && (
          <span className="flex items-center gap-0.5">
            <Zap className="w-3 h-3 text-amber-500" />
            ~{pin.wait_minutes}min wait
          </span>
        )}
        {pin.distance_miles != null && (
          <span className="flex items-center gap-0.5">
            <MapPin className="w-3 h-3 text-blue-400" />
            {pin.distance_miles}mi
          </span>
        )}
      </div>

      {pin.has_birthday_offer && (
        <div className="mt-2 px-2 py-1 bg-amber-50 border border-amber-200 rounded-md text-[10px] text-amber-700 font-semibold text-center">
          Birthday offer available
        </div>
      )}
    </div>
  );
}

export default function LiveTruckMap() {
  const navigate = useNavigate();
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userLoc, setUserLoc] = useState(null);
  const [center, setCenter] = useState({ lat: 38.58, lng: -121.49 }); // Sacramento default

  const fetchPins = useCallback(async (lat, lng) => {
    try {
      const params = {};
      if (lat != null && lng != null) {
        params.lat = lat;
        params.lng = lng;
        params.radius_miles = 25;
      }
      const res = await axios.get(`${API}/trucks/live-map`, { params });
      setPins(res.data.pins || []);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Try to get user location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          setUserLoc(loc);
          setCenter(loc);
          fetchPins(loc.lat, loc.lng);
        },
        () => fetchPins(null, null),
        { enableHighAccuracy: false, timeout: 5000 }
      );
    } else {
      fetchPins(null, null);
    }
  }, [fetchPins]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    // ═════════════════════════════════════════════════════════════
    // v1.0.0 Gate 7 Item 3 — SSE groundwork (polling active today)
    // Migration path:
    //   1) flip feature flag `live_truck_map_sse` to TRUE
    //   2) derive `cityKey` from userLoc / selected city (required path param)
    //   3) replace the setInterval below with:
    //        const es = new EventSource(`${API}/trucks/stream/${cityKey}`);
    //        es.addEventListener("truck_status_changed", () => fetchPins(userLoc?.lat, userLoc?.lng));
    //        es.addEventListener("vendor_session_changed", () => fetchPins(userLoc?.lat, userLoc?.lng));
    //        return () => es.close();
    //   4) keep initial fetchPins() call as hydration
    // Flag OFF → polling continues unchanged (60s interval below).
    // ═════════════════════════════════════════════════════════════
    const interval = setInterval(() => {
      fetchPins(userLoc?.lat, userLoc?.lng);
    }, 60000);
    return () => clearInterval(interval);
  }, [userLoc, fetchPins]);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="live-truck-map-page">
      {/* Header */}
      <div className="sticky top-0 z-[500] bg-white/90 backdrop-blur-md border-b border-slate-100 px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-1.5 rounded-lg hover:bg-slate-100 transition" data-testid="map-back-btn">
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-sm font-bold text-slate-800" data-testid="map-title">Live Food Trucks</h1>
          <p className="text-[10px] text-slate-400">
            {loading ? "Loading..." : `${pins.length} truck${pins.length !== 1 ? "s" : ""} live now`}
          </p>
        </div>
        <div className={`w-2 h-2 rounded-full ${pins.length > 0 ? "bg-green-500 animate-pulse" : "bg-slate-300"}`} />
      </div>

      {/* Map */}
      <div className="flex-1 relative">
        {loading && (
          <div className="absolute inset-0 z-[400] flex items-center justify-center bg-white/60">
            <Loader2 className="w-6 h-6 animate-spin text-green-500" />
          </div>
        )}

        <MapContainer
          center={[center.lat, center.lng]}
          zoom={12}
          className="w-full h-full"
          style={{ minHeight: "calc(100vh - 120px)" }}
          zoomControl={false}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <RecenterMap center={center} zoom={12} />

          {/* User location */}
          {userLoc && (
            <Marker position={[userLoc.lat, userLoc.lng]} icon={userIcon}>
              <Popup><span className="text-xs font-semibold text-blue-600">You are here</span></Popup>
            </Marker>
          )}

          {/* Live truck pins */}
          {pins.map((pin) => (
            <Marker key={pin.business_id} position={[pin.lat, pin.lng]} icon={liveTruckIcon}>
              <Popup maxWidth={260} className="live-truck-popup">
                <TruckPopup pin={pin} onNavigate={(bid) => navigate(`/truck/${bid}`)} />
              </Popup>
            </Marker>
          ))}
        </MapContainer>

        {/* Empty state overlay */}
        {!loading && pins.length === 0 && (
          <div className="absolute inset-0 z-[400] flex items-center justify-center pointer-events-none">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl shadow-lg border border-slate-100 p-6 text-center max-w-xs pointer-events-auto"
            >
              <div className="w-12 h-12 mx-auto rounded-xl bg-slate-100 flex items-center justify-center mb-3">
                <Truck className="w-6 h-6 text-slate-400" />
              </div>
              <p className="text-sm font-bold text-slate-700">No trucks live right now</p>
              <p className="text-xs text-slate-400 mt-1">Check back later — trucks go live throughout the day</p>
            </motion.div>
          </div>
        )}

        {/* Bottom truck list peek */}
        {pins.length > 0 && (
          <div className="absolute bottom-20 left-0 right-0 z-[400] px-3" data-testid="live-truck-list">
            <div className="overflow-x-auto pb-2 -mx-3 px-3">
              <div className="flex gap-2">
                {pins.map((pin) => (
                  <motion.div
                    key={pin.business_id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => navigate(`/truck/${pin.business_id}`)}
                    className="min-w-[200px] max-w-[200px] bg-white rounded-xl shadow-md border border-slate-100 p-3 flex-shrink-0 cursor-pointer"
                    data-testid={`live-truck-card-${pin.business_id}`}
                  >
                    <div className="flex items-center gap-2">
                      {pin.logo_url ? (
                        <img src={pin.logo_url} alt="" className="w-8 h-8 rounded-lg object-contain bg-slate-50" />
                      ) : (
                        <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center">
                          <Truck className="w-4 h-4 text-green-600" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-bold text-slate-800 truncate">{pin.name}</p>
                        <div className="flex items-center gap-1 text-[9px] text-green-600 font-semibold">
                          <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                          LIVE
                          {pin.distance_miles != null && (
                            <span className="text-slate-400 ml-1">{pin.distance_miles}mi</span>
                          )}
                        </div>
                      </div>
                    </div>
                    {pin.address && (
                      <div className="flex items-center gap-1 mt-1.5">
                        <Navigation className="w-2.5 h-2.5 text-green-500 shrink-0" style={{ transform: "rotate(45deg)" }} />
                        <p className="text-[9px] text-slate-500 truncate">{pin.address}</p>
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
